// File: app/src/utils/trainingHistory.js
//
// Summarises the user's recent completed sessions into a compact, bounded
// structure, and formats it into the optional "## Recent Training History"
// block injected into the LLM workout-generation prompt (see buildPrompt in
// app/src/apis/llmApi.js — issue #9).
//
// Generated Work nodes don't carry muscle-group metadata, so we summarise by
// exercise *label* + workout type + recency and let the model infer the
// muscle/movement distribution (it reads "Back Squat" as legs). Kept React-free
// (and out of llmApi.js) so it can be unit-tested without pulling in the
// provider SDK import chain — mirrors capabilityPrompt.js.
//
// A session need NOT have a plan tree. Training logged from outside the app
// (#77) — a run, a class, anything off a watch — has no exercises to name, and
// dropping those rows told the planner the user had rested when they had not.
// Such a session is summarised by its type, duration and whatever was measured
// (distance, average heart rate) instead.

import { describeActivity } from "./loggedSession";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// A session counts as "happened" if it was ended or has a recorded duration —
// in-progress/abandoned shells with neither are ignored.
export const sessionHappened = (s) =>
  !!s && (s.ended_at != null || Number(s.duration_minutes) > 0);

// Local midnight (epoch ms) for the day containing `ms`.
const startOfLocalDay = (ms) => {
  const d = new Date(ms);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};

// Whole *calendar* days between `started_at` and `now` in local time, min 0, so
// "yesterday" means the previous date on the wall calendar rather than merely
// ~24h ago — a Saturday-evening session reads as "2 days ago" on Monday morning,
// not "yesterday". Diffing local midnights (then rounding) stays correct across
// DST shifts. Returns null for an unparseable timestamp so the caller skips the row.
export const daysSince = (startedAt, now) => {
  const t = new Date(startedAt).getTime();
  if (!Number.isFinite(t)) return null;
  const diffDays = Math.round(
    (startOfLocalDay(now) - startOfLocalDay(t)) / MS_PER_DAY,
  );
  return Math.max(0, diffDays);
};

// Walk a plan tree collecting Work-node labels (deduped, case-insensitive,
// capped). Subtrees whose Group label looks like a warm-up/cool-down are
// skipped — their generic filler movements dilute the "what did I train" signal.
const collectExerciseLabels = (root, max) => {
  const out = [];
  const seen = new Set();

  const walk = (node) => {
    if (!node || out.length >= max) return;
    if (node.type === "Group") {
      if (/warm|cool/i.test(node.label || "")) return;
      if (Array.isArray(node.children)) node.children.forEach(walk);
      return;
    }
    if (node.type === "Work" && node.label) {
      const label = node.label.trim();
      const key = label.toLowerCase();
      if (key && !seen.has(key)) {
        seen.add(key);
        out.push(label);
      }
    }
  };

  // Iterate the root's children so the root Group's own label is never treated
  // as a warm-up/cool-down marker; fall back to walking a degenerate root.
  if (Array.isArray(root?.children)) root.children.forEach(walk);
  else walk(root);

  return out.slice(0, max);
};

/**
 * Build a bounded, most-recent-first summary of recently completed sessions.
 *
 * @param {Array} sessions  Rows from UserProfileContext (carry started_at,
 *   ended_at, duration_minutes, workout_type, plan_data, activity).
 * @param {Object} [opts]
 * @param {number} [opts.lookbackDays=7]  Inclusive day window.
 * @param {number} [opts.now=Date.now()]  Reference "now" (epoch ms) — injectable for tests.
 * @param {number} [opts.maxSessions=12]
 * @param {number} [opts.maxExercisesPerSession=10]
 * @returns {Array<{ daysAgo, workoutType, durationMinutes, exercises: string[], metrics: string }>}
 */
export const summariseTrainingHistory = (sessions, opts = {}) => {
  const {
    lookbackDays = 7,
    now = Date.now(),
    maxSessions = 12,
    maxExercisesPerSession = 10,
  } = opts;

  if (!Array.isArray(sessions) || sessions.length === 0) return [];

  const summary = [];
  for (const session of sessions) {
    if (!sessionHappened(session)) continue;

    const daysAgo = daysSince(session.started_at, now);
    if (daysAgo == null || daysAgo > lookbackDays) continue;

    const exercises = collectExerciseLabels(
      session.plan_data,
      maxExercisesPerSession,
    );
    const metrics = describeActivity(session.activity);

    // A row earns its place if it can say ANYTHING about the training: the
    // movements, the measurements, or failing both, that a session of this
    // type and length happened at all. Only a nameless, lengthless, empty
    // shell is skipped.
    const mins = Number(session.duration_minutes);
    const durationMinutes = Number.isFinite(mins) && mins > 0 ? mins : null;
    const workoutType =
      session.workout_type || session.plan_data?.label || "Workout";
    if (exercises.length === 0 && !metrics && durationMinutes == null) continue;

    const entry = { daysAgo, workoutType, durationMinutes, exercises, metrics };
    // The session's own "Counts as" choice (#173), for the weekly targets.
    if (Array.isArray(session.plan_data?.countsAs)) {
      entry.countsAs = session.plan_data.countsAs;
    }
    summary.push(entry);
  }

  // Sort most-recent-first regardless of input order, then cap.
  summary.sort((a, b) => a.daysAgo - b.daysAgo);
  return summary.slice(0, maxSessions);
};

const relativeDay = (daysAgo) => {
  if (daysAgo <= 0) return "today";
  if (daysAgo === 1) return "yesterday";
  return `${daysAgo} days ago`;
};

/**
 * Format the summary into the optional "## Recent Training History" prompt
 * block. Returns "" when there is no history, so the prompt stays byte-for-byte
 * identical to the legacy prompt for users with nothing recent (same guarantee
 * buildCapabilitiesSection gives).
 *
 * @param {Array} history  Output of summariseTrainingHistory().
 * @param {Object} [opts]
 * @param {number} [opts.lookbackDays=7]  Window, for the heading.
 * @returns {string}
 */
export const buildTrainingHistorySection = (history, opts = {}) => {
  if (!Array.isArray(history) || history.length === 0) return "";
  const { lookbackDays = 7 } = opts;

  const lines = history.map((h) => {
    const dur = h.durationMinutes ? `, ${h.durationMinutes} min` : "";
    // Exercises when the session had them; otherwise whatever was measured —
    // "8.0 km · 148 bpm avg" tells the planner as much about a run as a list of
    // movements does about a lift. With neither, the type and duration stand
    // alone rather than trailing an empty colon.
    const detail = h.exercises?.length
      ? `: ${h.exercises.join(", ")}`
      : h.metrics
        ? `: ${h.metrics}`
        : "";
    return `- ${relativeDay(h.daysAgo)} — ${h.workoutType || "Workout"}${dur}${detail}`;
  });

  return `
## Recent Training History (last ${lookbackDays} days)
The user has recently completed these sessions (most recent first):
${lines.join("\n")}

Use this to vary the training stimulus and avoid reloading muscle groups or movement patterns that were heavily trained in the last 1–2 days — bias toward complementary or recovery-oriented work to prevent overtraining and repetitive stress. This is guidance only: if the user's Target areas, Avoid list, or session notes explicitly request something, honour that request — explicit instructions always win.

After choosing the session, also set a top-level \`historyNote\` field on \`workout_plan\`: ONE plain sentence (≤ 18 words) telling the user how today's focus responds to the history above — e.g. "Chest-heavy week, so today leans legs and core." When you mention timing, use the day references exactly as written above (don't say "yesterday" unless a session is labelled yesterday). Base it only on a real difference; if recent history didn't change today's emphasis, omit the field. Don't mention that you were given history data.
`;
};

// Maximum stored length for a generated historyNote — long enough for one
// sentence, short enough to keep the planner chip a single legible line.
const HISTORY_NOTE_MAX_CHARS = 160;

/**
 * Clean an LLM-emitted `historyNote` for display: trim, collapse internal
 * whitespace, and cap length. Returns `null` for anything non-string or empty
 * so callers can treat absence and unusable values identically (the chip then
 * falls back to its generic "guided by your last N days" text).
 *
 * @param {*} value  Raw value off the parsed plan (may be undefined / non-string).
 * @returns {string|null}
 */
export const sanitiseHistoryNote = (value) => {
  if (typeof value !== "string") return null;
  const cleaned = value.replace(/\s+/g, " ").trim();
  if (!cleaned) return null;
  return cleaned.length > HISTORY_NOTE_MAX_CHARS
    ? `${cleaned.slice(0, HISTORY_NOTE_MAX_CHARS - 1).trimEnd()}…`
    : cleaned;
};
